import useNativeSmt from '@swarm/core/hooks/data/useNativeSmt'
import {
  formatBigInt,
  prettifyBalance,
} from '@swarm/core/shared/utils/formatting'
import {
  big,
  normalize,
  ZERO,
} from '@swarm/core/shared/utils/helpers/big-helpers'
import { useAccount } from '@swarm/core/web3'
import { ExtendedSecurity } from '@swarm/types/normalized-entities'
import Tooltip from '@swarm/ui/presentational/Tooltip'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex, Icon, Text } from 'rimble-ui'

import { calcStakedSecuritySMT } from '../utils'

interface StakingSummaryBlockProps {
  title: string
  value: string
  tooltip?: string
}

const RwaSummaryBlock = ({
  title,
  value,
  tooltip,
}: StakingSummaryBlockProps) => {
  return (
    <Box minWidth="200px" py="24px" mr="20px" textAlign="start">
      <Text color="primary" fontWeight={5} fontSize={3}>
        {value}
      </Text>

      <Flex flexWrap="nowrap" alignItems="center">
        <Text color="grey" fontSize={1}>
          {title}
        </Text>

        {tooltip && (
          <Tooltip placement="top" message={tooltip}>
            <Icon
              size="16px"
              name="Help"
              color="grey"
              ml={1}
              mt={-1}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </Flex>
    </Box>
  )
}

interface StakingSummaryProps {
  securities: ExtendedSecurity[]
}

const StakingSummary = ({ securities }: StakingSummaryProps) => {
  const { t } = useTranslation('staking')
  const account = useAccount()
  const { nativeSmt } = useNativeSmt()

  const totalTvl = useMemo(() => {
    return securities.reduce((sum, security) => {
      if (!security.marketCap) return big(0)
      return sum.plus(security.marketCap)
    }, big(0))
  }, [securities])

  const totalVolume = useMemo(() => {
    return securities.reduce((sum, security) => {
      if (!security.volume24hs) return sum
      return sum.plus(security.volume24hs)
    }, big(0))
  }, [securities])

  const totalMyStaked = useMemo(() => {
    if (!nativeSmt || !account) return ZERO
    return securities.reduce((smtStaked, security) => {
      const myStakedNormalized = normalize(
        calcStakedSecuritySMT(security),
        nativeSmt.decimals,
      )

      return smtStaked.plus(myStakedNormalized)
    }, ZERO)
  }, [account, nativeSmt, securities])

  const totalSmtStaked = useMemo(() => {
    if (!nativeSmt) return ZERO
    return securities.reduce(
      (smtStaked, security) =>
        security.smtStaked ? smtStaked.plus(security.smtStaked) : smtStaked,
      ZERO,
    )
  }, [nativeSmt, securities])

  const stakedLoading = !securities.length

  const loading =
    stakedLoading ||
    securities.some((security) => !security.marketCap || !security.smtStaked)

  const summary = useMemo(
    () => [
      {
        title: t('summary.totalMarketCap'),
        value: loading ? '--' : `$${formatBigInt(totalTvl)}`,
      },
      {
        title: t('summary.totalVolume'),
        tooltip: t('summary.volumeTooltip'),
        value: loading ? '--' : `$${formatBigInt(totalVolume)}`,
      },
      {
        title: t('summary.totalSmtStaked'),
        value: loading || !totalSmtStaked ? '--' : formatBigInt(totalSmtStaked),
      },
      {
        title: t('summary.myStaked'),
        value:
          loading || !totalMyStaked ? '--' : prettifyBalance(totalMyStaked),
      },
      {
        title: t('summary.myBalance'),
        value:
          loading || !nativeSmt?.balance
            ? '--'
            : formatBigInt(nativeSmt.balance),
      },
    ],
    [
      nativeSmt?.balance,
      t,
      totalMyStaked,
      totalSmtStaked,
      totalTvl,
      totalVolume,
      loading,
    ],
  )

  return (
    <Flex justifyContent="space-between" minHeight="100px" flexWrap="wrap">
      {summary.map(({ title, value, tooltip }) => (
        <RwaSummaryBlock
          key={`staking_${title}_${value.toString()}`}
          title={title}
          value={value}
          tooltip={tooltip}
        />
      ))}
    </Flex>
  )
}

export default StakingSummary
